import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { Loading } from "@/components/ui/loading";
import { useAuth } from "@/features/auth/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { de, enUS, es } from "date-fns/locale";
import { useTranslation } from "react-i18next";

export interface Comment {
  title: string;
  text: string;
  added_at: string; // ISO 8601 timestamp string is typical
  added_by: string;
  added_by_name: string;
}

export interface InternalNotes {
  notes: Comment[];
}

interface NotesAndCommentsProps {
  isTalentProfile: boolean;
  talentOrApplicationId: string;
}

export function NotesAndComments({
  isTalentProfile,
  talentOrApplicationId
}: NotesAndCommentsProps) {
  const { profile } = useAuth();
  const { t, i18n } = useTranslation(["common"]);
  const [internalNotes, setInternalNotes] = useState<InternalNotes | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [noteTitle, setNoteTitle] = useState("");
  const [noteText, setNoteText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const fetchNotes = useCallback(async () => {
    if (!talentOrApplicationId) return;

    setLoading(true);
    setError(null);

    try {
      const table = isTalentProfile ? "talent_profiles" : "talent_applications";

      const { data, error: fetchError } = await supabase
        .from(table)
        .select("internal_notes")
        .eq("id", talentOrApplicationId)
        .single();

      if (fetchError) throw fetchError;
      setInternalNotes(data?.internal_notes as InternalNotes | null);
    } catch (err: any) {
      console.error("Error fetching notes:", err);
      setError(t("notes.messages.error_fetch"));
    } finally {
      setLoading(false);
    }
  }, [isTalentProfile, talentOrApplicationId, t]);

  useEffect(() => {
    fetchNotes();
  }, [fetchNotes]);

  const UpdatesNotes = async (updatedNotes: Comment[]) => {
    try {
      const table = isTalentProfile ? "talent_profiles" : "talent_applications";
      const newInternalNotes: InternalNotes = { notes: updatedNotes };

      const { error: updateError } = await supabase
        .from(table)
        .update({ internal_notes: newInternalNotes as any }) // Supabase expects Json
        .eq("id", talentOrApplicationId);

      if (updateError) throw updateError;

      setInternalNotes(newInternalNotes);
    } catch (err: any) {
      console.error("Error updating notes:", err);
      throw err;
    }
  };

  const AddNote = async (note: Comment) => {
    const currentNotes = internalNotes?.notes || [];
    const newNotes = [...currentNotes, note];
    await UpdatesNotes(newNotes);
  };

  const DeleteNote = async (notes: Comment[], indexToDelete: number) => {
    if (!window.confirm(t("notes.messages.confirm_delete"))) return;
    const newNotes = notes.filter((_, index) => index !== indexToDelete);
    await UpdatesNotes(newNotes);
  };

  const handleSaveNote = async () => {
    if (!noteTitle.trim() || !noteText.trim()) return;

    setIsSubmitting(true);
    try {
      if (editingIndex !== null && internalNotes?.notes) {
        // Edit existing note
        const updatedNotes = [...internalNotes.notes];
        updatedNotes[editingIndex] = {
          ...updatedNotes[editingIndex],
          title: noteTitle.trim(),
          text: noteText.trim()
        };
        await UpdatesNotes(updatedNotes);
      } else {
        // Add new note
        const newNote: Comment = {
          title: noteTitle.trim(),
          text: noteText.trim(),
          added_at: new Date().toISOString(),
          added_by: profile.id,
          added_by_name:
            `${profile.given_name || ""} ${profile.family_name || ""}`.trim() ||
            t("roles.superuser")
        };
        await AddNote(newNote);
      }
      setIsDialogOpen(false);
    } catch (err) {
      console.error("Failed to save note:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDialogForNew = () => {
    setNoteTitle("");
    setNoteText("");
    setEditingIndex(null);
    setIsDialogOpen(true);
  };

  const openDialogForEdit = (note: Comment, index: number) => {
    setNoteTitle(note.title);
    setNoteText(note.text);
    setEditingIndex(index);
    setIsDialogOpen(true);
  };

  const getDateLocale = () => {
    const lang = i18n.language || "en";
    if (lang.startsWith("de")) return de;
    if (lang.startsWith("es")) return es;
    return enUS;
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <Loading size="sm" text={t("notes.loading")} />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-sm p-4">{error}</div>;
  }

  const notes = internalNotes?.notes || [];
  // Sort notes by date descending (newest first)
  const sortedNotesWithIndex = notes
    .map((note, idx) => ({ note, originalIndex: idx }))
    .sort(
      (a, b) =>
        new Date(b.note.added_at).getTime() -
        new Date(a.note.added_at).getTime()
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">
          {t("notes.title")}
        </h3>
        <Button onClick={openDialogForNew} size="sm" className="gap-2">
          <Plus className="h-4 w-4" />
          {t("notes.add_note")}
        </Button>
      </div>

      {sortedNotesWithIndex.length > 0 ? (
        <div className="relative border-l-2 border-gray-200 ml-3 space-y-8 pb-4">
          {sortedNotesWithIndex.map(({ note, originalIndex }) => (
            <div key={originalIndex} className="relative pl-6">
              {/* Timeline Dot */}
              <div className="absolute w-4 h-4 bg-primary rounded-full -left-[9px] top-4 border-4 border-white shadow-sm"></div>

              {/* Card */}
              <div className="p-5 bg-white border border-gray-100 shadow-sm rounded-xl transition-all hover:shadow-md">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">
                      {note.title}
                    </h4>
                    <p className="text-xs text-gray-500 mt-1">
                      {t("notes.meta.added_by")}{" "}
                      <span className="font-medium text-gray-700">
                        {note.added_by_name}
                      </span>{" "}
                      •{" "}
                      {formatDistanceToNow(new Date(note.added_at), {
                        addSuffix: true,
                        locale: getDateLocale()
                      })}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-gray-900"
                      onClick={() => openDialogForEdit(note, originalIndex)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-red-600"
                      onClick={() => DeleteNote(notes, originalIndex)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="text-sm text-gray-700 whitespace-pre-wrap">
                  {note.text}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12 px-4 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
          <p className="text-sm text-gray-500 mb-4">{t("notes.empty_state")}</p>
          <Button
            variant="outline"
            onClick={openDialogForNew}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            {t("notes.add_first_note")}
          </Button>
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingIndex !== null
                ? t("notes.edit_title")
                : t("notes.create_title")}
            </DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label
                htmlFor="title"
                className="text-sm font-medium leading-none"
              >
                {t("notes.form.title_label")}
              </label>
              <Input
                id="title"
                placeholder={t("notes.form.title_placeholder")}
                value={noteTitle}
                onChange={(e) => setNoteTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <label
                htmlFor="text"
                className="text-sm font-medium leading-none"
              >
                {t("notes.form.text_label")}
              </label>
              <Textarea
                id="text"
                placeholder={t("notes.form.text_placeholder")}
                className="min-h-[120px] resize-none"
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              onClick={handleSaveNote}
              disabled={isSubmitting || !noteTitle.trim() || !noteText.trim()}
            >
              {isSubmitting ? t("status.saving") : t("notes.form.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
